import { Box, Button, Container, Heading, Text, VStack, useToast, Alert, AlertIcon, Spinner } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import ErrorBoundary from '../../../components/ErrorBoundary';
import MarkdownRenderer from '../../../components/MarkdownRenderer';
import { supabase } from '../../../lib/supabase';
import { useCourse } from '../../../contexts/CourseContext';
import { useCourseConfig } from '../../../hooks/useCourseConfig';

// Custom error fallback for course page
const RMIT1LandingErrorFallback = ({ error, resetErrorBoundary }: { error: Error | null, resetErrorBoundary: () => void }) => {
  const navigate = useNavigate();
  const [isReloading, setIsReloading] = useState(false);

  const handleReset = () => {
    setIsReloading(true);
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <Container maxW="container.lg" py={8}>
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box flex="1">
          <Heading size="md" mb={2}>Course Loading Error</Heading>
          <Text>We're having trouble loading the course content. Please try refreshing the page or contact support if the issue persists.</Text>
          {process.env.NODE_ENV === 'development' && error && (
            <Box mt={2} p={2} bg="blackAlpha.100" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold">Error details:</Text>
              <Text as="pre" whiteSpace="pre-wrap" fontFamily="mono">
                {error.message}
              </Text>
            </Box>
          )}
        </Box>
      </Alert>
      <VStack spacing={4}>
        <Button
          colorScheme="blue"
          onClick={handleReset}
          isLoading={isReloading}
          loadingText="Reloading..."
        >
          Reload Page
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          isDisabled={isReloading}
        >
          Return to Home
        </Button>
      </VStack>
    </Container>
  );
};

function RMIT1LandingContent() {
  const navigate = useNavigate();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentCourse } = useCourse();
  const { landing } = useCourseConfig();

  const handleConsent = async () => {
    setIsSubmitting(true);
    try {
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      if (!currentCourse) {
        throw new Error('No course assigned to user');
      }

      const { error } = await supabase
        .from('course_learner_preferences')
        .upsert(
          {
            user_id: user.id,
            course_id: currentCourse.id,
            consent_given: true,
            consent_timestamp: new Date().toISOString(),
          },
          { onConflict: 'user_id,course_id', ignoreDuplicates: false }
        );

      if (error) throw error;

      toast({
        title: 'Consent recorded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/preferences');
    } catch (error) {
      console.error('Error recording consent:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast({
        title: 'Error recording consent',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Split markdown content into sections
  const content = landing?.copy ?? `# Rad Learning Tutor  

*Welcome RMIT students!*  

## ⚡ TL;DR – What you need to know  

- 🗣️ Chat with the AI tutor **about your presentation skills**  
- 🎯 It helps you **reflect and prepare**, but will not write your presentation  
- 🔒 Chats are **recorded**, but only used for learning. **Anonymous data** may be shared with RMIT  

## 🎤 Presentation Skills Tutor  

### What is it?  
The **Presentation Skills Tutor** is an AI chat activity. It will **ask you questions** about your upcoming presentation and your skills. The goal is to **help you reflect** on what you already know and what you can improve before next week.  

### Why are we doing this?  
**Presentation skills are important** for your project, your study, and your future career. The tutor will **not create your presentation** for you. Instead, it will help you **think, practice, and build confidence** 💪.  

### How does it work?  
- 🗓️ You can chat with the tutor **this week only**  
- 💬 It will use your course materials to ask and respond to questions  
- ✅ This activity is **not assessed**  
- 🔒 Your chat is **not part of your university record**. It is only to **support your learning**  

## ✅ Consent to Having Your Conversation Recorded  

Our tutor is a great listener! 🗣️✨  

By chatting, you agree that:  
- **Rad Learning will record and review your conversation** with the Tutor  
- This is **only to support learning** and improve the Rad Learning experience  
- **Anonymous data** (no names attached) may be **shared with RMIT staff** to help improve teaching  
- Your chat is **not assessed** and will **not affect your grades**  `;

  const idxTLDR = content.indexOf('## ⚡ TL;DR');
  const idxTutor = content.indexOf('## Presentation Skills Tutor');
  const idxConsent = content.indexOf('## Consent');
  const safe = (i: number) => (i >= 0 ? i : Number.MAX_SAFE_INTEGER);

  const headerEnd = Math.min(safe(idxTLDR), safe(idxTutor), safe(idxConsent), content.length);
  const headerBlock = content.slice(0, headerEnd).trim();

  const tldrEnd = Math.min(safe(idxTutor), safe(idxConsent), content.length);
  const tldrBlock = idxTLDR >= 0 ? content.slice(idxTLDR, tldrEnd).trim() : '';

  const tutorEnd = Math.min(safe(idxConsent), content.length);
  const tutorBlock = idxTutor >= 0 ? content.slice(idxTutor, tutorEnd).trim() : '';

  const consentBlock = idxConsent >= 0 ? content.slice(idxConsent).trim() : '';
  let consentHeadingText = 'Consent to having your interactions recorded';
  let consentBody = '';
  if (consentBlock) {
    const lines = consentBlock.split('\n');
    const headingLine = lines[0] || '';
    consentHeadingText = headingLine.replace(/^##\s*/, '').trim() || consentHeadingText;
    consentBody = lines.slice(1).join('\n').trim();
  }

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="4xl">
        {/* Title and subtitle at the top */}
        {headerBlock && (
          <VStack spacing={4} textAlign="center" mb={6}>
            <MarkdownRenderer content={headerBlock} />
          </VStack>
        )}

        {/* TL;DR in its own purple outlined box */}
        {tldrBlock && (
          <Box
            bg="white"
            p={8}
            borderRadius="xl"
            mb={8}
            borderWidth="2px"
            borderColor="#C200FB"
            boxShadow="sm"
          >
            <MarkdownRenderer content={tldrBlock} />
          </Box>
        )}

        {/* Presentation Skills Tutor section (standard content box) */}
        {tutorBlock && (
          <Box
            bg="white"
            p={8}
            borderRadius="xl"
            mb={8}
            boxShadow="sm"
            borderWidth="2px"
            borderColor="#C200FB"
          >
            <MarkdownRenderer content={tutorBlock} />
          </Box>
        )}

        {/* Consent section: heading + text inside purple box, above the button */}
        <Box
          bg="white"
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="#C200FB"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={4}>
            {consentHeadingText}
          </Heading>
          {consentBody && <MarkdownRenderer content={consentBody} />}

          <Button
            mt={4}
            size="lg"
            bg="#C200FB"
            color="white"
            _hover={{ bg: '#A500D9' }}
            onClick={handleConsent}
            isLoading={isSubmitting}
            loadingText="Processing..."
            rightIcon={<span>→</span>}
          >
            I consent to recording this conversation
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default function RMIT1LandingPage() {
  const { currentCourse, isLoading } = useCourse();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!currentCourse) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="xl" mt={6} mb={2}>
          No Course Assigned
        </Heading>
        <Text color={'gray.500'} mb={6}>
          You haven't been assigned to a course yet. Please contact your administrator.
        </Text>
        <Button
          colorScheme="purple"
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </Button>
      </Box>
    );
  }

  return (
    <ErrorBoundary
      fallback={<RMIT1LandingErrorFallback error={null} resetErrorBoundary={() => window.location.reload()} />}
    >
      <RMIT1LandingContent />
    </ErrorBoundary>
  );
}
